import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import FileDown from 'lucide-react/dist/esm/icons/file-down';
import { useAmlStore } from '../../stores/amlStore';
import { amlApi } from '../../services/amlApi';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import {
  Button, Text, Checkbox, Spinner,
  Table, Thead, Tbody, Tr, Th, Td,
} from '../../../../components/ui';
import type { AmlCustomer } from '../../types/aml';

type FieldKey =
  | 'initial_date'
  | 'code'
  | 'name'
  | 'ein_or_fein'
  | 'registration_number'
  | 'established_date'
  | 'legal_representative'
  | 'address'
  | 'locations'
  | 'service_and_product'
  | 'activity_type'
  | 'contact'
  | 'email'
  | 'phone';

const KYC_FIELD_KEYS: FieldKey[] = [
  'initial_date', 'code', 'name', 'ein_or_fein', 'registration_number',
  'established_date', 'legal_representative', 'address', 'locations',
  'service_and_product', 'activity_type', 'contact', 'email', 'phone',
];

const KYC_LABEL_KEYS: Record<FieldKey, string> = {
  initial_date: 'amlKycFieldInitialDate',
  code: 'amlKycFieldCode',
  name: 'amlKycFieldCustomerLegalName',
  ein_or_fein: 'amlKycFieldEinFein',
  registration_number: 'amlKycFieldRegistrationNumber',
  established_date: 'amlKycFieldDateCompanyEstablished',
  legal_representative: 'amlKycFieldCompanyLegalRepresentative',
  address: 'amlKycFieldMainAddress',
  locations: 'amlKycFieldLocationsDetail',
  service_and_product: 'amlKycFieldServicesProduct',
  activity_type: 'amlKycFieldTypeOfBusinessActivity',
  contact: 'amlKycFieldContact',
  email: 'amlKycFieldEmail',
  phone: 'amlKycFieldPhone',
};

function getFieldValue(customer: AmlCustomer, key: FieldKey): string {
  if (key === 'locations') {
    return customer.locations?.map((l) => l.name).join(', ') || '';
  }
  const val = customer[key as keyof AmlCustomer];
  if (val === null || val === undefined) return '';
  return String(val);
}

function isExpiredDate(dateStr: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return d < now;
}

function buildInitialStatus(customer: AmlCustomer): Record<string, boolean> {
  const status: Record<string, boolean> = {};
  for (const key of KYC_FIELD_KEYS) {
    status[key] = customer.data_status?.[key] !== undefined
      ? customer.data_status[key]
      : !!getFieldValue(customer, key);
  }
  return status;
}

function hasStatusDelta(
  incoming: Record<string, boolean>,
  existing: Record<string, boolean> | undefined | null,
): boolean {
  if (!existing) return true;
  for (const key of KYC_FIELD_KEYS) {
    if (incoming[key] !== existing[key]) return true;
  }
  return false;
}

interface CustomerKycViewProps {
  customer: AmlCustomer;
}

const CustomerKycView: React.FC<CustomerKycViewProps> = ({ customer }) => {
  const { updateCustomerPartial } = useAmlStore();
  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);

  const kycFields = useMemo(
    () => KYC_FIELD_KEYS.map((key) => ({ key, label: t(KYC_LABEL_KEYS[key]) })),
    [t],
  );

  const [downloading, setDownloading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [dataStatus, setDataStatus] = useState<Record<string, boolean>>({});
  const [additionalNotes, setAdditionalNotes] = useState<Record<string, string>>({});

  const dataStatusRef = useRef(dataStatus);
  dataStatusRef.current = dataStatus;
  const additionalNotesRef = useRef(additionalNotes);
  additionalNotesRef.current = additionalNotes;

  useEffect(() => {
    const initialStatus = buildInitialStatus(customer);

    setDataStatus(initialStatus);
    setAdditionalNotes({ ...(customer.additional_notes ?? {}) });

    if (hasStatusDelta(initialStatus, customer.data_status)) {
      updateCustomerPartial(customer.id, { data_status: initialStatus }).then(() => {
        setInitialized(true);
      }).catch(() => {
        setInitialized(true);
      });
    } else {
      setInitialized(true);
    }
  }, [customer.id, customer.data_status, customer.additional_notes, updateCustomerPartial]);

  const handleCheckboxChange = useCallback(
    (field: string, checked: boolean) => {
      setDataStatus((prev) => {
        const next = { ...prev, [field]: checked };
        updateCustomerPartial(customer.id, { data_status: next });
        return next;
      });
    },
    [customer.id, updateCustomerPartial],
  );

  const handleNotesBlur = useCallback(
    (field: string, value: string) => {
      setAdditionalNotes((prev) => {
        const next = { ...prev, [field]: value };
        updateCustomerPartial(customer.id, { additional_notes: next });
        return next;
      });
    },
    [customer.id, updateCustomerPartial],
  );

  const handleExport = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await amlApi.downloadKyc(customer.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${customer.name.replace(/\s+/g, '-').toLowerCase()}-kyc-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // handled by API layer
    } finally {
      setDownloading(false);
    }
  }, [customer.id, customer.name]);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div />
        <Button
          variant="primary"
          size="md"
          leftIcon={<FileDown size={16} />}
          loading={downloading}
          onClick={handleExport}
        >
          {t('amlKycBtnExportReport')}
        </Button>
      </div>

      <div className="rounded-xl border border-subtle overflow-hidden">
        <Table>
          <Thead>
            <Tr className="!bg-brand text-content-inverse">
              <Th className="!text-content-inverse !font-semibold !normal-case !tracking-normal !text-sm whitespace-nowrap">
                {t('amlKycTableHeaderDataReviewedByCO')}
              </Th>
              <Th className="!text-content-inverse !font-semibold !normal-case !tracking-normal !text-sm">
                {customer.name || 'MISSING'}
              </Th>
              <Th className="!text-content-inverse !font-semibold !normal-case !tracking-normal !text-sm text-center w-[60px] whitespace-nowrap">
                {t('amlKycTableHeaderData')}
              </Th>
              <Th className="!text-content-inverse !font-semibold !normal-case !tracking-normal !text-sm whitespace-nowrap">
                {t('amlKycTableHeaderAdditionalNotes')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {kycFields.map(({ key, label }) => {
              const val = getFieldValue(customer, key);
              const displayVal = val || 'MISSING';
              const isInitialDateExpired = key === 'initial_date' && isExpiredDate(val);

              return (
                <Tr key={key}>
                  <Td className="font-medium whitespace-nowrap">{label}</Td>
                  <Td
                    className={`break-words ${isInitialDateExpired ? '!text-status-error font-medium' : ''}`}
                    style={{ whiteSpace: 'normal' }}
                  >
                    {displayVal}
                  </Td>
                  <Td className="text-center">
                    <Checkbox
                      checked={!!dataStatus[key]}
                      onChange={(checked) => handleCheckboxChange(key, checked)}
                    />
                  </Td>
                  <Td>
                    <textarea
                      className="w-full min-h-[38px] px-2 py-1.5 text-sm rounded-lg bg-surface-primary border border-subtle text-content placeholder-content-muted outline-none focus:border-brand/40 transition-colors resize-none"
                      maxLength={100}
                      defaultValue={additionalNotes[key] ?? ''}
                      onBlur={(e) => handleNotesBlur(key, e.target.value)}
                      placeholder=""
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </div>

      <Text variant="body" className="text-content-secondary">
        <span className="font-medium">{t('amlKycDateReviewed')}</span>{' '}
        <span>{today}</span>
      </Text>
    </div>
  );
};

export default CustomerKycView;
